import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useWarehouseDetail } from '../hooks/useWarehouses';
import { Button } from '../components/ui/Button';
import { WarehouseDetailsTab } from '../components/warehouse/WarehouseDetailsTab';
import { WarehouseZonesTab } from '../components/warehouse/WarehouseZonesTab';
import { WarehouseRacksTab } from '../components/warehouse/WarehouseRacksTab';
import { WarehouseMapTab } from '../components/warehouse/WarehouseMapTab';
import { formatDimensions } from '../utils/warehouseUtils';
import type { WarehouseTab } from '../types/warehouse';
import {
  ArrowLeft,
  Warehouse as WarehouseIcon,
  Settings,
  Layers,
  Grid3X3,
  Map,
  Loader2,
  AlertCircle,
  Box,
} from 'lucide-react';

const TABS: { id: WarehouseTab; label: string; icon: React.FC<{ className?: string }> }[] = [
  { id: 'details', label: 'Details', icon: Settings },
  { id: 'zones', label: 'Zones', icon: Layers },
  { id: 'racks', label: 'Racks', icon: Grid3X3 },
  { id: 'map', label: 'Visual Map', icon: Map },
];

export const WarehouseDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<WarehouseTab>('details');

  const {
    warehouse,
    zones,
    racks,
    bins,
    statistics,
    loading,
    error,
    updateWarehouse,
    deleteWarehouse,
    createZone,
    updateZone,
    deleteZone,
    createRack,
    updateRack,
    deleteRack,
    duplicateRacks,
    layout,
    updateLayout,
  } = useWarehouseDetail(id ?? null);

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Loading warehouse...</p>
        </div>
      </div>
    );
  }

  if (error || !warehouse) {
    return (
      <div className="p-6">
        <div className="max-w-lg mx-auto text-center py-16">
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            Warehouse Not Found
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {error || 'The warehouse you are looking for does not exist or has been archived.'}
          </p>
          <Button variant="secondary" onClick={() => navigate('/settings')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Settings
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <button
          onClick={() => navigate('/settings')}
          className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors mb-4"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Settings
        </button>

        <div className="flex items-start justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center">
              <WarehouseIcon className="h-7 w-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {warehouse.name}
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                {formatDimensions(warehouse.length, warehouse.width, warehouse.unit)}
                {warehouse.height && ` x ${warehouse.height} ${warehouse.unit} height`}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-6">
            <StatBadge icon={Layers} label="Zones" value={statistics.total_zones} color="blue" />
            <StatBadge icon={Grid3X3} label="Racks" value={statistics.total_racks} color="green" />
            <StatBadge icon={Box} label="Bins" value={statistics.total_bins} color="amber" />
          </div>
        </div>
      </div>

      <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
        <nav className="flex gap-1 overflow-x-auto">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors whitespace-nowrap ${
                  isActive
                    ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:border-gray-300'
                }`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      <div className="card p-6">
        {activeTab === 'details' && (
          <WarehouseDetailsTab
            warehouse={warehouse}
            statistics={statistics}
            onUpdate={updateWarehouse}
            onDelete={deleteWarehouse}
          />
        )}
        {activeTab === 'zones' && (
          <WarehouseZonesTab
            zones={zones}
            racks={racks}
            unit={warehouse.unit}
            onCreateZone={createZone}
            onUpdateZone={updateZone}
            onDeleteZone={deleteZone}
          />
        )}
        {activeTab === 'racks' && (
          <WarehouseRacksTab
            zones={zones}
            racks={racks}
            bins={bins}
            unit={warehouse.unit}
            onCreateRack={createRack}
            onUpdateRack={updateRack}
            onDeleteRack={deleteRack}
            onDuplicateRacks={duplicateRacks}
          />
        )}
        {activeTab === 'map' && (
          <WarehouseMapTab
            warehouse={warehouse}
            zones={zones}
            racks={racks}
            bins={bins}
            layout={layout}
            onUpdateLayout={async (viewport) => {
              await updateLayout({ viewport_json: viewport });
            }}
            onUpdateZone={updateZone}
            onUpdateRack={updateRack}
            onCreateZone={async (data) => {
              const { warehouse_id, ...zoneData } = data;
              return createZone(zoneData);
            }}
            onCreateRack={async (data) => {
              const { warehouse_id, ...rackData } = data;
              return createRack(rackData);
            }}
          />
        )}
      </div>
    </div>
  );
};

interface StatBadgeProps {
  icon: React.FC<{ className?: string }>;
  label: string;
  value: number;
  color: 'blue' | 'green' | 'amber';
}

const StatBadge: React.FC<StatBadgeProps> = ({ icon: Icon, label, value, color }) => {
  const colors = {
    blue: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
    green: 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400',
    amber: 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400',
  };

  return (
    <div className="flex items-center gap-2">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colors[color]}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <div className="text-xl font-bold text-gray-900 dark:text-gray-100">{value}</div>
        <div className="text-xs text-gray-500 dark:text-gray-400">{label}</div>
      </div>
    </div>
  );
};

